import { Component, OnInit, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonComponent, ListTableComponent, PopupDialogComponent } from '@ppl/ui';
import { UserService } from '../../../../../services/user.service';
import { NotificationService } from '../../../../../shared/services/notification.service';
import { TimesheetPayrollProcessingService } from '../timesheet-payroll-processing.service';

const EXCLUDE_ACTION = 'excludePayrollBatchRows';

/**
 * A row can only be pulled out of the batch while it is still queued. Matched against the
 * description the API sends in lineBatchStatus.
 */
const EXCLUDABLE_LINE_BATCH_STATUSES = ['ready to stage', 'ready for payroll'];

/** Case and inner spacing vary between environments, so statuses are compared normalised. */
function normalizeStatus(value: unknown): string {
  return String(value ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

@Component({
  selector: 'ppl-timesheet-payroll-run-results',
  standalone: true,
  imports: [FormsModule, ListTableComponent, ButtonComponent, PopupDialogComponent],
  templateUrl: './timesheet-payroll-run-results.component.html',
  styleUrl: './timesheet-payroll-run-results.component.css',
})
export class TimesheetPayrollRunResultsComponent implements OnInit {
  @ViewChild(ListTableComponent) listTable!: ListTableComponent;

  programName = '';
  batchInstanceId = '';
  results: any[] = [];
  recordsPerPage = 50;

  showExcludeDialog = false;
  comments = '';
  selectedRows: any[] = [];
  isSubmitting = false;

  /**
   * batch-results returns no column metadata, so the grid is given explicit
   * columns for the documented response fields.
   */
  cols = [
    { field: 'payrollBatchId', header: 'Batch Row ID', jsonpath: 'payrollBatchId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'lineId', header: 'Line ID', jsonpath: 'lineId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'providerId', header: 'Provider ID', jsonpath: 'providerId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'employerId', header: 'Employer ID', jsonpath: 'employerId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'consumerId', header: 'Consumer ID', jsonpath: 'consumerId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'serviceDate', header: 'Service Date', jsonpath: 'serviceDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'serviceCode', header: 'Service Code', jsonpath: 'serviceCode', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'paidUnits', header: 'Paid Units', jsonpath: 'paidUnits', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'servicecodeRate', header: 'Rate', jsonpath: 'servicecodeRate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'lineProcessingStatus', header: 'Processing Status', jsonpath: 'lineProcessingStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'lineBusinessStatus', header: 'Business Status', jsonpath: 'lineBusinessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
    { field: 'lineBatchStatus', header: 'Batch Status', jsonpath: 'lineBatchStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
  ];

  /** Supplied imperatively because this grid is not driven by a list-page config. */
  buttons = [
    {
      button_id: 'exclude',
      button_label: 'Exclude',
      buttonAction: EXCLUDE_ACTION,
      className: 'primary',
    },
  ];

  /** True when the row's batch status still allows it to be excluded. */
  isExcludable = (row: any): boolean =>
    EXCLUDABLE_LINE_BATCH_STATUSES.includes(normalizeStatus(row?.lineBatchStatus));

  constructor(
    private userService: UserService,
    private router: Router,
    private notificationService: NotificationService,
    private payrollService: TimesheetPayrollProcessingService
  ) {}

  ngOnInit(): void {
    const selectedProgram = this.userService.getSelectedProgram();
    this.programName = selectedProgram?.program_code ?? '';
    this.batchInstanceId =
      this.router.getCurrentNavigation()?.extras?.state?.['batchInstanceId'] ?? history.state?.batchInstanceId ?? '';
    this.loadResults(0);
  }

  ngAfterViewInit(): void {
    if (this.listTable) {
      this.listTable.listButton = this.buttons;
    }
  }

  onPageLoad(event: any): void {
    const rows = event?.rows ?? this.recordsPerPage;
    const pageNumber = Math.max(Number(event?.startingPageNumber) || 1, 1);
    this.recordsPerPage = rows;
    this.loadResults((pageNumber - 1) * rows, pageNumber > 1);
  }

  /** batch-results gives no total, so pages are accumulated as they load. */
  private loadResults(offset: number, append = false): void {
    if (!this.programName || !this.batchInstanceId) {
      return;
    }

    this.payrollService
      .getBatchResults(this.programName, this.batchInstanceId, offset, this.recordsPerPage)
      .subscribe({
        next: (batchRows) => {
          // ppl-list-table renders `processingStatus` / `businessStatus` columns as
          // timesheet drilldown links for some values, so they are shown under other keys.
          const rows = batchRows.map((row) => ({
            ...row,
            lineProcessingStatus: row.processingStatus,
            lineBusinessStatus: row.businessStatus,
          }));
          this.results = append ? [...this.results, ...rows] : rows;
        },
        error: () => {
          this.notificationService.error('Error', 'Failed to load payroll batch results');
        },
      });
  }

  onListAction(event: any): void {
    if (event?.button?.buttonAction !== EXCLUDE_ACTION) {
      return;
    }
    const selected = event?.selectedCheckBox ?? [];

    // The checkbox is already disabled for an ineligible row, so this catches a selection that
    // outlived the status it was made against — the grid reloads after every exclude.
    if (selected.some((row: any) => !this.isExcludable(row))) {
      this.notificationService.error(
        'Error',
        'Only rows with a batch status of Ready To Stage or Ready For Payroll can be excluded.'
      );
      return;
    }

    this.selectedRows = selected;
    this.comments = '';
    this.showExcludeDialog = true;
  }

  onExcludeConfirm(): void {
    if (this.isExcludeBlocked) {
      return;
    }

    const rowIds = this.selectedRows.map((row) => Number(row.payrollBatchId)).filter((id) => !Number.isNaN(id));

    this.isSubmitting = true;
    this.payrollService.excludeRows(this.programName, this.batchInstanceId, rowIds, this.comments).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.showExcludeDialog = false;
        this.notificationService.success('Success', 'Rows excluded successfully');
        this.loadResults(0);
      },
      error: () => {
        this.isSubmitting = false;
        this.showExcludeDialog = false;
        this.notificationService.error('Error', 'Failed to exclude the selected rows');
      },
    });
  }

  get isExcludeBlocked(): boolean {
    return (
      this.isSubmitting ||
      !this.selectedRows.length ||
      !this.comments.trim() ||
      this.selectedRows.some((row) => !this.isExcludable(row))
    );
  }

  goBack(): void {
    this.router.navigate(['/home/timesheetPayrollProcessing']);
  }
}
